import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { Popconfirm, message } from "antd";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import { createSegment } from "../../../../services/EngagementAPIs";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import { useTranslation } from "react-i18next";

type ConditionType = "list" | "action" | "";

interface Condition {
  id: number;
  type: ConditionType;
  /** When type is "list", whether membership is positive or negative (drives dropdown + API). */
  listMatchMode?: "in" | "not-in";
  selectedValues: string[];
}

const CreateSegment: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [conditions, setConditions] = useState<Condition[]>([
    { id: 1, type: "list", listMatchMode: "in", selectedValues: [] },
  ]);
  const [segmentName, setSegmentName] = useState("");
  const [loading, setLoading] = useState(false);
  const tenantId = localStorage.getItem("tenant_id") || "";
  // operators[i] is the logical operator joining conditions[i] and conditions[i + 1].
  // Tracked per junction (instead of one shared value) so picking an operator for a
  // new condition doesn't retroactively change an operator already chosen earlier.
  const [operators, setOperators] = useState<("and" | "or")[]>([]);

  const addCondition = () => {
    const newId =
      conditions.length > 0
        ? Math.max(...conditions.map(condition => condition.id)) + 1
        : 1;
    setConditions([...conditions, { id: newId, type: "", selectedValues: [] }]);
  };

  const addConditionWithOperator = (nextOperator: "and" | "or") => {
    setOperators(prev => [...prev, nextOperator]);
    addCondition();
  };

  const removeCondition = (id: number) => {
    const index = conditions.findIndex(cond => cond.id === id);
    if (index === -1) {
      return;
    }

    setConditions(prev => prev.filter(cond => cond.id !== id));
    setOperators(prev => {
      // Drop the junction right after the removed condition, falling back to
      // the one before it when the removed condition was the last in the list.
      const junctionIndex = index < prev.length ? index : index - 1;
      return junctionIndex < 0 ? prev : prev.filter((_, i) => i !== junctionIndex);
    });
  };

  const setListConditionMode = (id: number, mode: "in" | "not-in") => {
    setConditions(prev =>
      prev.map(cond =>
        cond.id === id
          ? { ...cond, type: "list", listMatchMode: mode, selectedValues: [] }
          : cond
      )
    );
  };

  const toggleConditionValue = (id: number, value: string) => {
    setConditions(prev =>
      prev.map(cond =>
        cond.id === id
          ? {
              ...cond,
              selectedValues: cond.selectedValues.includes(value)
                ? [] // unselect if already selected
                : [value], // replace with the new single value
            }
          : cond
      )
    );
  };

  const ConditionButton = ({
    value,
    label,
    conditionId,
    selectedValues,
    disabled,
  }: {
    value: string;
    label: string;
    conditionId: number;
    selectedValues: string[];
    disabled: boolean;
  }) => {
    const isActive = selectedValues.includes(value);
    return (
      <button
        type="button"
        className={`condition-btn ${isActive ? "active" : ""}`}
        onClick={() => !disabled && toggleConditionValue(conditionId, value)}
        disabled={disabled}
      >
        {label}
      </button>
    );
  };

  const getAllSelectedValues = (): string[] => {
    return conditions.flatMap(cond => cond.selectedValues);
  };

  const listOptions = [
    {
      value: "High Spenders",
      label: t("audienceOverview.createSegment.listOptions.highSpenders"),
    },
    {
      value: "Cart Abandoners",
      label: t("audienceOverview.createSegment.listOptions.cartAbandoners"),
    },
    {
      value: "Loyal Customers",
      label: t("audienceOverview.createSegment.listOptions.loyalCustomers"),
    },
    {
      value: "Likely to Churn",
      label: t("audienceOverview.createSegment.listOptions.likelyToChurn"),
    },
    {
      value: "Subscribers",
      label: t("audienceOverview.createSegment.listOptions.subscribers"),
    },
    {
      value: "Idle Customers",
      label: t("audienceOverview.createSegment.listOptions.idleCustomers"),
    },
    {
      value: "Indecisive Customers",
      label: t("audienceOverview.createSegment.listOptions.indecisiveCustomers"),
    },
  ];

  // handleCreateSegment
const handleCreateSegment = async () => {
  if (!segmentName.trim()) {
    message.error(t("audienceOverview.createSegment.segmentNameRequired"));
    return;
  }

  const allSelected = getAllSelectedValues();
  if (allSelected.length < 2) {
    message.error(t("audienceOverview.createSegment.selectAtLeastTwo"));
    return;
  }

  // Convert conditions into backend structure
  const conditionsPayload = conditions.map(cond => ({
    condition_type:
      cond.type === "list"
        ? cond.listMatchMode === "not-in"
          ? "not_in_list"
          : "in_list"
        : "action",
    list_names: cond.selectedValues,
  }));

  const criteriaPayload = {
    conditions: conditionsPayload,
    operators,
  };


  setLoading(true);
  try {
    await createSegment(tenantId, segmentName, criteriaPayload);
    message.success(t("audienceOverview.createSegment.createSuccess"));
    navigate("/account/engagement/select-list-or-segment");
  } catch {
    message.error(t("audienceOverview.createSegment.createFailed"));
  } finally {
    setLoading(false);
  }
};



  // Highlights whichever operator was chosen most recently so the AND/OR
  // controls give immediate feedback; defaults to "and" before any choice.
  const activeOperator = operators[operators.length - 1] ?? "and";

  return (
    <div className="create-segment-container create-segment-page">
      <button
        className="button button--primary create-segment-back-button"
        onClick={() => navigate("/account/engagement/select-list-or-segment")}
      >
        {t("audienceOverview.createSegment.back")}
      </button>

      <h1 className="create-segment-title">
        {t("audienceOverview.createSegment.title")}
      </h1>
      <p className="create-segment-subtitle">
        {t("audienceOverview.createSegment.subtitle")}
      </p>

      <div className="create-segment-form">
        <label className="create-segment-label">
          {t("audienceOverview.createSegment.segmentNameLabel")}
        </label>
        <StyledTextInput
          className="segment-name-input create-segment-input"
          placeholder={t("audienceOverview.createSegment.segmentNamePlaceholder")}
          value={segmentName}
          onChange={e => setSegmentName(e.target.value)}
        />

        <h3 className="create-segment-label create-segment-conditions-title">
          {t("audienceOverview.createSegment.conditionalStatements")}
        </h3>

        {conditions.map((cond, index) => (
          <div key={cond.id} className="create-segment-condition-wrap">
            <div className="create-segment-condition-card">
              <div className="segment-conditions">
                <StyledDropdown
                  className="condition-dropdown create-segment-condition-dropdown"
                  placeholder={t("audienceOverview.createSegment.chooseListCondition")}
                  value={
                    cond.type === "list"
                      ? cond.listMatchMode ?? "in"
                      : undefined
                  }
                  onChange={value => {
                    if (value === "in" || value === "not-in") {
                      setListConditionMode(cond.id, value);
                    }
                  }}
                  options={[
                    {
                      label: t("audienceOverview.createSegment.ifInList"),
                      value: "in",
                    },
                    {
                      label: t("audienceOverview.createSegment.ifNotInList"),
                      value: "not-in",
                    },
                  ]}
                />

                {cond.type === "list" && (
                  <>
                    <h3 className="create-segment-list-title">
                      {t("audienceOverview.createSegment.chooseFromList")}
                    </h3>
                    <div className="condition-row create-segment-chip-row">
                      {listOptions.map(({ value, label }) => {
                        const allSelected = getAllSelectedValues();
                        const isDisabled =
                          allSelected.includes(value) &&
                          !cond.selectedValues.includes(value);

                        return (
                          <ConditionButton
                            key={value}
                            value={value}
                            label={label}
                            conditionId={cond.id}
                            selectedValues={cond.selectedValues}
                            disabled={isDisabled}
                          />
                        );
                      })}
                    </div>
                  </>
                )}

                {conditions.length > 1 && cond.type !== "" ? (
                  <div className="create-segment-delete-row">
                    <Popconfirm
                      title={t("audienceOverview.createSegment.confirmDelete")}
                      onConfirm={() => removeCondition(cond.id)}
                      okText={t("audienceOverview.createSegment.yes")}
                      cancelText={t("audienceOverview.createSegment.no")}
                      placement="topRight"
                    >
                      <button
                        type="button"
                        className="create-segment-delete-btn"
                        aria-label={t("audienceOverview.createSegment.deleteConditionAria")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </Popconfirm>
                  </div>
                ) : null}

                <div className="create-segment-card-bottom">
                  <div className="create-segment-logic-buttons">
                    <button
                      type="button"
                      className={`button button--outline ${
                        activeOperator === "and" ? "is-active" : ""
                      }`}
                      onClick={() => addConditionWithOperator("and")}
                    >
                      {t("audienceOverview.createSegment.and")}
                    </button>
                    <button
                      type="button"
                      className={`button button--outline ${
                        activeOperator === "or" ? "is-active" : ""
                      }`}
                      onClick={() => addConditionWithOperator("or")}
                    >
                      {t("audienceOverview.createSegment.or")}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {index < conditions.length - 1 ? (
              <div className="create-segment-connector">
                <span className="create-segment-connector-pill">
                  {operators[index] === "and"
                    ? t("audienceOverview.createSegment.and")
                    : t("audienceOverview.createSegment.or")}
                </span>
              </div>
            ) : null}
          </div>
        ))}

        <div className="create-segment-actions">
          <button
            className="button button--primary"
            onClick={handleCreateSegment}
            disabled={loading}
          >
            {t("audienceOverview.createSegment.createSegment")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSegment;
